// ── 급여 상세/조정 모달 ──────────────────────────────────────────────────────
// 개별 직원의 급여 항목 상세 보기 + 수동 조정 기능
// 확정 전(calculated) 상태에서만 금액 수정 가능

import React, { useState, useEffect } from 'react';
import { X, Printer, Save } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { printPayslip } from './payslipPrint';

// ── 모바일 감지 훅 (768px 이하) ─────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '0';
const parse = (s) => Number(String(s).replace(/,/g, '')) || 0;
const hasNonZeroAmount = (item) => Number(item?.amount) !== 0;
const isDeductionRuleEnabled = (rules, key) => rules?.[key]?.enabled === true;
const workDaysWithPaidLeave = (record) => {
  const paid = Number(record?.paid_leave_days) || 0;
  const work = record?.work_days ?? 0;
  return paid > 0 ? `${work}일 (유급 ${paid}일)` : `${work}일`;
};

export default function PayrollRecordModal({ recordId, isConfirmed, period, companyName, onClose, onSaved }) {
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // 수정용 금액 맵: { [record_item_id]: 금액 문자열 }
  const [editMap, setEditMap] = useState({});

  // ── 상세 데이터 로드 ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apiClient.get(`/api/admin/payroll/monthly/records/${recordId}`);
      if (res.ok && res.data?.data) {
        setDetail(res.data.data);
        const map = {};
        res.data.data.items.forEach((it) => { map[it.id] = fmt(it.amount); });
        setEditMap(map);
      }
      setLoading(false);
    })();
  }, [recordId]);

  // ── 저장 ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    const items = detail.items.map((it) => ({ id: it.id, amount: parse(editMap[it.id]) }));
    const res = await apiClient.put(`/api/admin/payroll/monthly/records/${recordId}`, { items });
    setSaving(false);
    if (res.ok) {
      showToast('급여가 조정되었습니다.', 'success');
      onSaved?.();
      onClose();
    } else {
      showToast(res.data?.message || '조정 실패', 'error');
    }
  };

  // ── 로딩 상태 ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center" {...overlayCloseProps}>
      <div className={cn('bg-[var(--bg-card)] rounded-xl p-10 text-center text-sm text-[var(--text-muted)]',
        isMobile && 'rounded-none w-full h-full flex items-center justify-center')} onClick={(e) => e.stopPropagation()}>
        로딩 중...
      </div>
    </div>
  );
  if (!detail) return null;

  // 지급·공제 분리
  const editable = !isConfirmed;
  const visibleItems = editable ? detail.items : detail.items.filter(hasNonZeroAmount);
  const earnings = visibleItems.filter((i) => i.item_type === 'earning');
  const deductions = visibleItems.filter((i) => i.item_type === 'deduction');

  // 합계 계산 (편집 중이면 editMap 기준)
  let totE = 0, totD = 0;
  detail.items.forEach((it) => {
    const amt = editable ? parse(editMap[it.id]) : Number(it.amount);
    if (it.item_type === 'earning') totE += amt; else totD += amt;
  });

  // 통상임금 시급
  const hourly = detail.monthly_hours ? Math.floor(detail.ordinary_wage / detail.monthly_hours) : 0;

  // 총 근로시간
  const workMins = Number(detail.total_work_minutes) || 0;
  const workH = Math.floor(workMins / 60);
  const workM = workMins % 60;

  // 근태 요약 항목 — "출근 없음"(attendance_time_type=NULL)이면 만근 표시
  const noAtt = !detail.attendance_time_type;
  const deductionRules = detail.deduction_rules || {};
  const statItems = [
    { label: '소정근로일', value: noAtt ? '해당없음' : `${detail.biz_days || 0}일` },
    { label: '출근일(유급휴가)', value: noAtt ? '해당없음' : workDaysWithPaidLeave(detail) },
    { label: '결근일', value: noAtt ? '-' : `${detail.absent_days}일`, warn: !noAtt && detail.absent_days > 0 },
    { label: '총 근로시간', value: noAtt ? '-' : `${workH}h ${workM}m` },
    { label: '지각', value: `${detail.late_count}회${detail.late_minutes > 0 ? ` (${detail.late_minutes}분)` : ''}`, warn: detail.late_count > 0 },
    { label: '조퇴', value: `${detail.early_leave_count}회${detail.early_leave_minutes > 0 ? ` (${detail.early_leave_minutes}분)` : ''}`, warn: detail.early_leave_count > 0 },
    { label: '무급휴가', value: `${detail.unpaid_leave_days}일`, warn: detail.unpaid_leave_days > 0 },
    { label: '주휴공제', value: detail.weekly_holiday_weeks > 0 ? `${detail.weekly_holiday_weeks}주` : '-', warn: detail.weekly_holiday_weeks > 0 },
  ];

  // ── 항목 행 렌더러 (지급/공제 공통 — 중복 제거용) ──
  const visibleStatItems = statItems.filter((_, index) => ([
    true,
    true,
    isDeductionRuleEnabled(deductionRules, 'absent'),
    true,
    isDeductionRuleEnabled(deductionRules, 'late'),
    isDeductionRuleEnabled(deductionRules, 'early_leave'),
    isDeductionRuleEnabled(deductionRules, 'unpaid_leave'),
    true,
  ])[index] !== false);

  const renderItem = (it, isDed) => {
    const amount = editable ? parse(editMap[it.id]) : Number(it.amount);
    const isNegative = amount < 0;
    return (
      <div key={it.id} className="flex items-center justify-between py-1.5 px-1 border-b border-[var(--border)]/50 last:border-0">
        <span className="text-[11px] text-[var(--text-sub)]">{it.item_name}</span>
        {editable ? (
          <input
            className={cn(
              'w-24 h-7 px-2 rounded-md text-xs text-right font-semibold bg-white dark:bg-pine-800 border border-[var(--border)] dark:border-pine-600 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-400/20',
              isNegative ? 'text-red-600 dark:text-red-400' : 'text-[var(--text)]',
            )}
            value={editMap[it.id] || '0'}
            onChange={(e) => setEditMap({ ...editMap, [it.id]: e.target.value })}
            onBlur={() => setEditMap({ ...editMap, [it.id]: fmt(parse(editMap[it.id])) })}
          />
        ) : (
          <span className={cn('text-xs font-semibold tabular-nums', (isDed || isNegative) ? 'text-red-600 dark:text-red-400' : 'text-[var(--text)]')}>{fmt(it.amount)}원</span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center" {...overlayCloseProps}>
      <div className={cn(
        'bg-[var(--bg-card)] overflow-y-auto',
        isMobile
          ? 'w-full h-full p-4'
          : 'w-full max-w-[560px] max-h-[90vh] rounded-xl border border-[var(--border)] shadow-2xl p-5',
      )} onClick={(e) => e.stopPropagation()}>

        {/* ── 헤더 ── */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-base font-extrabold text-[var(--text)] leading-tight">{detail.user_name}</h3>
            <span className="text-[11px] text-[var(--text-muted)]">급여 상세</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--bg-subtle)] transition-colors text-[var(--text-muted)]">
            <X size={16} />
          </button>
        </div>

        {/* ── 통상임금 ── */}
        {detail.ordinary_wage > 0 && (
          <div className="flex items-center justify-between px-3 py-1.5 mb-2 rounded-lg bg-accent-500/5 dark:bg-accent-500/10 border border-accent-200/30 dark:border-accent-500/20">
            <span className="text-[11px] font-semibold text-accent-600 dark:text-accent-400">통상임금</span>
            <div className="text-right">
              <span className="text-xs font-bold text-[var(--text)]">{fmt(detail.ordinary_wage)}원</span>
              {detail.monthly_hours
                ? <span className="text-[10px] text-[var(--text-muted)] ml-1.5">(시급 {fmt(hourly)}원)</span>
                : <Badge variant="red" className="ml-1.5 text-[9px]">템플릿 없음</Badge>}
            </div>
          </div>
        )}

        {/* ── 근태 요약 ── */}
        <div className={cn('grid gap-1.5 mb-2', isMobile ? 'grid-cols-2' : 'grid-cols-4')}>
          {visibleStatItems.map((s) => (
            <div key={s.label} className={cn('flex flex-col items-center px-1 rounded-lg bg-[var(--bg-subtle)]', isMobile ? 'py-2' : 'py-1')}>
              <span className="text-[9px] font-medium text-[var(--text-muted)] mb-0.5">{s.label}</span>
              <span className={cn('text-xs font-bold', s.warn ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--text-sub)]')}>{s.value}</span>
            </div>
          ))}
        </div>

        {/* ── 지급/공제 항목 (데스크톱: 2열 좌우, 모바일: 1열 순차) ── */}
        {isMobile ? (
          <>
            <div className="mb-2">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">지급 항목</span>
              </div>
              {earnings.map((it) => renderItem(it, false))}
            </div>
            <div className="mb-2">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span className="text-xs font-bold text-red-600 dark:text-red-400">공제 항목</span>
              </div>
              {deductions.map((it) => renderItem(it, true))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">지급 항목</span>
              </div>
              {earnings.map((it) => renderItem(it, false))}
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span className="text-xs font-bold text-red-600 dark:text-red-400">공제 항목</span>
              </div>
              {deductions.map((it) => renderItem(it, true))}
            </div>
          </div>
        )}

        {/* ── 합계 요약 ── */}
        <div className="rounded-xl overflow-hidden border border-[var(--border)] mb-2">
          {/* 지급/공제 합계 */}
          <div className="grid grid-cols-2 divide-x divide-[var(--border)]">
            <div className="p-3 text-center bg-[var(--bg-subtle)]">
              <div className="text-[9px] font-medium text-[var(--text-muted)] mb-0.5">지급 합계</div>
              <div className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 tabular-nums">{fmt(totE)}</div>
            </div>
            <div className="p-3 text-center bg-[var(--bg-subtle)]">
              <div className="text-[9px] font-medium text-[var(--text-muted)] mb-0.5">공제 합계</div>
              <div className="text-sm font-extrabold text-red-600 dark:text-red-400 tabular-nums">{fmt(totD)}</div>
            </div>
          </div>
          {/* 실수령액 */}
          <div className="flex items-center justify-between px-4 py-2 bg-pine-900 dark:bg-pine-800">
            <span className="text-xs font-semibold text-white/70">실수령액</span>
            <span className="text-lg font-black text-white tabular-nums">{fmt(totE - totD)}원</span>
          </div>
        </div>

        {/* ── 수동 조정 표시 ── */}
        {detail.is_adjusted && (
          <div className="mb-2">
            <Badge variant="yellow" className="text-[10px]">수동 조정됨</Badge>
          </div>
        )}

        {/* ── 하단 버튼 ── */}
        <div className={cn('flex gap-1.5', isMobile ? 'flex-col' : 'justify-end')}>
          {/* 명세서 인쇄 (좌측 정렬) */}
          {period && (
            <Button variant="outline" size="sm" className={cn(!isMobile && 'mr-auto')} onClick={() => printPayslip(recordId, period, companyName)}>
              <Printer size={13} />
              명세서 인쇄
            </Button>
          )}
          {editable && (
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              <Save size={13} />
              {saving ? '저장 중...' : '조정 저장'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>닫기</Button>
        </div>
      </div>
    </div>
  );
}
